import { useState } from 'react'
import { BookOpen, Briefcase, CheckCircle2, Play } from 'lucide-react'

import { Button } from '@/components'
import { cn } from '@/utils/cn'

type EnrollmentStatus = 'active' | 'trial' | 'completed'

interface Enrollment {
  id: string
  childName: string
  grade: string
  code: string
  courseTitle: string
  schedule: string
  level: string
  status: EnrollmentStatus
  statusLabel: string
  statusTone: 'success' | 'warning' | 'info'
  progress: number
  nextClassLabel: string
}

const ENROLLMENTS: Enrollment[] = [
  {
    id: '1',
    childName: 'Sarah Johnson',
    grade: 'Grade 5',
    code: 'CAABCD12',
    courseTitle: 'Python for Beginners',
    schedule: 'Private 1-on-1 · Wed & Fri · 4:30 PM',
    level: 'Gold Level',
    status: 'active',
    statusLabel: 'On track',
    statusTone: 'success',
    progress: 55,
    nextClassLabel: 'Next class: Today · 4:30 PM',
  },
  {
    id: '2',
    childName: 'Michael Johnson',
    grade: 'Grade 5',
    code: 'CAABCD34',
    courseTitle: 'Web Development',
    schedule: 'Group · Sat · 10:00 AM',
    level: 'Gold Level',
    status: 'trial',
    statusLabel: 'Trial · 3 days left',
    statusTone: 'warning',
    progress: 30,
    nextClassLabel: 'Next class: Sat · 10:00 AM',
  },
  {
    id: '3',
    childName: 'Emily Johnson',
    grade: 'Grade 3',
    code: 'CAABCD56',
    courseTitle: 'Scratch Game Design',
    schedule: 'Private 1-on-1 · Sun · 3:00 PM',
    level: 'Silver Level',
    status: 'completed',
    statusLabel: 'Completed',
    statusTone: 'info',
    progress: 100,
    nextClassLabel: 'Completed · Certificate issued',
  },
]

type EnrollmentTab = 'all' | 'active' | 'completed'

export const ParentEnrollments: React.FC = () => {
  const [tab, setTab] = useState<EnrollmentTab>('all')

  const activeCount = ENROLLMENTS.filter(
    e => e.status === 'active' || e.status === 'trial'
  ).length
  const completedCount = ENROLLMENTS.filter(
    e => e.status === 'completed'
  ).length
  const totalCount = ENROLLMENTS.length

  const filtered = ENROLLMENTS.filter(enrollment => {
    if (tab === 'all') return true
    if (tab === 'active')
      return enrollment.status === 'active' || enrollment.status === 'trial'
    return enrollment.status === 'completed'
  })

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Enrollments</h1>
          <p className="mt-1 text-sm text-gray-500">
            View and manage all course enrollments for your children.
          </p>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <div className="flex items-center justify-between rounded-2xl border-2 border-[#D0EAD9] bg-[#F4F8F7] p-6 shadow-sm">
          <div>
            <p className="text-xs font-medium text-gray-500">
              Active Enrollments
            </p>
            <p className="mt-1 text-2xl font-bold text-gray-900">
              {activeCount}
            </p>
          </div>
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#E2F1E8] text-[#34A853]">
            <Play className="h-4 w-4 fill-current" />
          </div>
        </div>
        <div className="flex items-center justify-between rounded-2xl border-2 border-[#C7D5E8] bg-[#F5F7F9] p-6 shadow-sm">
          <div>
            <p className="text-xs font-medium text-gray-500">
              Completed Courses
            </p>
            <p className="mt-1 text-2xl font-bold text-gray-900">
              {completedCount}
            </p>
          </div>
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#E1E8F2] text-[#1D5AA6]">
            <CheckCircle2 className="h-4 w-4" />
          </div>
        </div>
        <div className="flex items-center justify-between rounded-2xl border-2 border-[#F7E0C9] bg-[#F9F7F5] p-6 shadow-sm">
          <div>
            <p className="text-xs font-medium text-gray-500">
              Total Enrollments
            </p>
            <p className="mt-1 text-2xl font-bold text-gray-900">
              {totalCount}
            </p>
          </div>
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#F28C28]/15 text-[#F28C28]">
            <Briefcase className="h-4 w-4" />
          </div>
        </div>
      </div>

      <div className="border-b border-gray-200">
        <nav className="flex gap-6 text-sm" aria-label="Enrollment filters">
          <button
            type="button"
            onClick={() => setTab('all')}
            className={cn(
              'border-b-2 pb-3 pt-0.5 font-medium transition-colors',
              tab === 'all'
                ? 'border-[#F28C28] text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
          >
            All Enrollments
          </button>
          <button
            type="button"
            onClick={() => setTab('active')}
            className={cn(
              'border-b-2 pb-3 pt-0.5 font-medium transition-colors',
              tab === 'active'
                ? 'border-[#F28C28] text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
          >
            Active ({activeCount})
          </button>
          <button
            type="button"
            onClick={() => setTab('completed')}
            className={cn(
              'border-b-2 pb-3 pt-0.5 font-medium transition-colors',
              tab === 'completed'
                ? 'border-[#F28C28] text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
          >
            Completed ({completedCount})
          </button>
        </nav>
      </div>

      <div className="">
        <div className="space-y-4">
          {filtered.map(enrollment => {
            return (
              <div
                key={enrollment.id}
                className="flex flex-col gap-4 rounded-2xl bg-white border border-gray-100 px-4 py-4 sm:px-5 sm:py-5 lg:flex-row lg:items-center"
              >
                <div className="flex flex-1 flex-col gap-3">
                  <div className="flex flex-1 shrink-0 items-start justify-between gap-3">
                    <div className="min-w-0 space-y-4">
                      <div className="flex items-center gap-2">
                        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-amber-100 text-sm font-semibold text-amber-800 shadow-sm">
                          {enrollment.childName
                            .split(' ')
                            .map(n => n[0])
                            .join('')}
                        </div>
                        <div className="flex flex-col items-start gap-1">
                          <span className="text-base font-normal text-[#222222]">
                            {enrollment.courseTitle}
                          </span>
                          <p className="text-sm font-semibold text-gray-900">
                            {enrollment.childName}
                          </p>
                        </div>
                      </div>
                      <span className="inline-flex items-center px-2.5 py-1 mt-2 rounded-full bg-white text-black border border-gray-200 text-xs font-medium shrink-0">
                        Group class
                      </span>

                      <p className="mt-2 flex items-center gap-2 bg-white text-xs font-normal text-gray-800">
                        <BookOpen className="h-3 w-3 text-gray-800" />
                        {'Instructor: Mr. David Chen'}
                      </p>
                      <p className="mt-2 flex items-center gap-2 bg-white text-xs font-normal text-gray-800">
                        <BookOpen className="h-3 w-3 text-gray-800" />
                        {'Wed & Fri, 4:30 PM'}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        {'Lessons: 17/25'}
                      </p>
                    </div>

                    {/* Middle: progress bar */}
                    <div className="mt-3 w-full max-w-xs sm:mt-0 sm:pl-4 flex items-center gap-5">
                      <span>{'Started September 1, 2024'}</span>
                      <button
                        type="button"
                        className="text-xs text-white bg-[#34A853] px-4 py-2.5 rounded-2xl flex items-center gap-2"
                      >
                        <Play className="h-4 w-4" />
                        Active
                      </button>
                    </div>
                  </div>

                  {/* Progress bar Here */}
                  <div className="mt-1 w-full">
                    <div className="flex items-center justify-between text-xs text-gray-600">
                      <span></span>
                      <span className="font-semibold">
                        {enrollment.progress}%
                      </span>
                    </div>
                    <div className="mt-1.5 h-2 w-full rounded-full bg-gray-200">
                      <div
                        className="h-2 rounded-full bg-black/70"
                        style={{ width: `${enrollment.progress}%` }}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-start gap-3 bg-[#F7F8FA] border-2 border-[#F9E3CD] rounded-2xl p-6">
                  <span
                    className={cn(
                      'flex flex-col items-start gap-1 rounded-full py-1 font-medium'
                    )}
                  >
                    <span className="text-base font-normal text-[#222222]">
                      Next Class
                    </span>
                    <span className="text-base font-normal text-[#222222]">
                      Wed, Dec 4, 4:30 PM
                    </span>
                  </span>

                  <div className="flex flex-col gap-2 w-full">
                    <Button
                      type="button"
                      variant="primary"
                      className="bg-[#1D5AA6] px-4 py-2 text-xs font-semibold hover:bg-blue-800 focus:ring-blue-600"
                    >
                      Join Class
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      className="border border-gray-200 bg-white px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 !min-w-[120px]"
                    >
                      Cancel Class
                    </Button>
                  </div>
                </div>
              </div>
            )
          })}

          {filtered.length === 0 && (
            <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-6 py-10 text-center text-sm text-gray-500">
              No enrollments found in this view yet.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
